import dayjs from 'dayjs';

export type WorkspaceRouteStopSlice = {
  type?: string | null;
  position?: number | null;
  plannedAt?: string | null;
  actualAt?: string | null;
};

export type WorkspaceRouteDateFallback = {
  plannedLoadingDate?: string | null;
  plannedUnloadingDate?: string | null;
  actualLoadingDate?: string | null;
  actualUnloadingDate?: string | null;
};

export type CompactStopDatesSummary = {
  planLine: string;
  factLine: string;
  planTooltipLines: string[];
  factTooltipLines: string[];
  stopCount: number;
};

function formatDayMonth(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM') : null;
}

function stopTypeNorm(type: string | null | undefined): string {
  return String(type || '').trim().toLowerCase();
}

function pickStops(stops: WorkspaceRouteStopSlice[], kind: 'loading' | 'unloading'): WorkspaceRouteStopSlice[] {
  return [...stops]
    .filter((s) => stopTypeNorm(s.type) === kind)
    .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0));
}

export function summarizeStopKindDates(
  stops: WorkspaceRouteStopSlice[] | null | undefined,
  kind: 'loading' | 'unloading',
  fallback: { planned: string | null | undefined; actual: string | null | undefined }
): CompactStopDatesSummary {
  const matched = pickStops(stops ?? [], kind);

  if (matched.length === 0) {
    const fp = formatDayMonth(fallback.planned);
    const ff = formatDayMonth(fallback.actual);
    return {
      planLine: fp ?? '—',
      factLine: ff ?? '—',
      planTooltipLines: fp ? [fp] : [],
      factTooltipLines: ff ? [ff] : [],
      stopCount: 0,
    };
  }

  const primaryPlan = formatDayMonth(matched[0]?.plannedAt) ?? '—';
  const overflow = Math.max(0, matched.length - 1);
  const planLine = overflow > 0 ? `${primaryPlan} (+${overflow})` : primaryPlan;

  const primaryFact =
    formatDayMonth(matched[0]?.actualAt) ?? formatDayMonth(fallback.actual);
  const factLine = primaryFact ?? '—';

  const planTooltipLines = matched.map((s, index) => {
    const plan = formatDayMonth(s.plannedAt) ?? '—';
    return matched.length > 1 ? `${index + 1}. ${plan}` : plan;
  });

  const factTooltipLines = matched.map((s, index) => {
    const fact = formatDayMonth(s.actualAt) ?? formatDayMonth(fallback.actual) ?? '—';
    return matched.length > 1 ? `${index + 1}. ${fact}` : fact;
  });

  return {
    planLine,
    factLine,
    planTooltipLines,
    factTooltipLines,
    stopCount: matched.length,
  };
}

export function workspaceRouteSortTimestamp(
  route: WorkspaceRouteDateFallback & { createdAt?: string | null; stops?: WorkspaceRouteStopSlice[] | null }
): number {
  const firstStop = pickStops(route.stops ?? [], 'loading')[0];
  const fromStop = firstStop?.plannedAt ? dayjs(firstStop.plannedAt).valueOf() : 0;
  const fromRoute = route.plannedLoadingDate ? dayjs(route.plannedLoadingDate).valueOf() : 0;
  const fromCreated = route.createdAt ? dayjs(route.createdAt).valueOf() : 0;
  return fromStop || fromRoute || fromCreated || 0;
}
